// OTA 1.0.4 (2026-06-19) — Root-level error boundary.
//
// Before this boundary existed, ANY uncaught render error in ANY
// React subtree would unmount the entire React root and leave the
// WebView showing a stark white/black blank screen with no way out
// for the user.  This was the systemic cause of the "click then
// blank" reports across multiple flows (FeaturedVendorsSection
// `.map` crash, post-location-grant render crashes, axios response-
// shape regressions in vendor lists, etc.).
//
// What this gives us:
//   1. A recoverable UI instead of a blank screen.  User sees the
//      WashPros logo, a friendly message, and TWO recovery actions
//      ("Try Again" reloads the React tree in place; "Reload App"
//      forces a full reload via location.reload() — works on
//      native WebView).
//   2. An expandable "Show technical details" block (matches the
//      OTA 1.0.3 auth diagnostic surface) revealing the error
//      message, component stack, current pathname, backend host,
//      timestamp.  Tap "Copy details" → 8-field envelope in
//      clipboard, ready to paste into support.
//   3. STRICT sanitization — same invariant as auth diagnostic:
//      never include passwords, tokens, Authorization headers,
//      Set-Cookie, full URLs with query strings, or PII.
//
// This is intentionally a CLASS COMPONENT (the React error-boundary
// API requires `componentDidCatch` / `getDerivedStateFromError`
// which only work on classes as of React 19.x).

import React from "react";

function safeBackendHost() {
  try {
    const raw = process.env.REACT_APP_BACKEND_URL || "";
    if (!raw) return "(unset)";
    return new URL(raw).host;
  } catch {
    return "(invalid)";
  }
}

function safePathname() {
  try {
    if (typeof window === "undefined") return "(no-window)";
    return window.location?.pathname || "/";
  } catch {
    return "(unknown)";
  }
}

function buildSanitizedEnvelope(error, info) {
  const errorMessage = (() => {
    try {
      const m = error?.message;
      return typeof m === "string" ? m.slice(0, 500) : String(error).slice(0, 500);
    } catch {
      return "(unreadable error)";
    }
  })();
  const componentStack = (() => {
    try {
      const cs = info?.componentStack;
      // Trim to first 600 chars — enough to identify the failing component, never
      // contains user input / PII.
      return typeof cs === "string" ? cs.slice(0, 600).trim() : null;
    } catch {
      return null;
    }
  })();
  return {
    message: errorMessage,
    name: error?.name || "Error",
    componentStack,
    pathname: safePathname(),
    backendHost: safeBackendHost(),
    userAgent: (typeof navigator !== "undefined" && navigator.userAgent
      ? navigator.userAgent.slice(0, 200)
      : "(no-ua)"),
    ts: new Date().toISOString(),
  };
}

function serializeEnvelope(env) {
  if (!env) return "";
  const lines = [
    `message: ${env.message}`,
    `name: ${env.name}`,
    `pathname: ${env.pathname}`,
    `backend: ${env.backendHost}`,
    `user-agent: ${env.userAgent}`,
    `ts: ${env.ts}`,
  ];
  if (env.componentStack) lines.push(`stack: ${env.componentStack}`);
  return lines.join("\n");
}

async function copyEnvelope(text) {
  if (!text) return false;
  try {
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (e) { void e; }
  try {
    if (typeof document === "undefined") return false;
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.cssText = "position:fixed;top:-9999px;left:-9999px;opacity:0;";
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return !!ok;
  } catch (e) { void e; return false; }
}

class RootErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      envelope: null,
      showDetails: false,
      copyStatus: "idle", // idle | copied | failed
      retryKey: 0,
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, envelope: { message: error?.message || String(error), name: error?.name || "Error" } };
  }

  componentDidCatch(error, info) {
    const envelope = buildSanitizedEnvelope(error, info);
    this.setState({ envelope });
    // Sanitized console log — same envelope shape, no secrets.
    try {
      console.error("[root-error-boundary] render crash", envelope);
    } catch (e) { void e; }
  }

  handleTryAgain = () => {
    // Reset the boundary so the React subtree gets a chance to re-render.
    // Bumping retryKey forces children to re-mount fresh.
    this.setState((s) => ({
      hasError: false,
      envelope: null,
      showDetails: false,
      copyStatus: "idle",
      retryKey: s.retryKey + 1,
    }));
  };

  handleHardReload = () => {
    try {
      if (typeof window !== "undefined" && window.location?.reload) {
        window.location.reload();
      }
    } catch (e) { void e; }
  };

  handleCopy = async () => {
    const ok = await copyEnvelope(serializeEnvelope(this.state.envelope));
    this.setState({ copyStatus: ok ? "copied" : "failed" });
    setTimeout(() => this.setState({ copyStatus: "idle" }), 1600);
  };

  render() {
    if (!this.state.hasError) {
      // Important: pass retryKey via the React key so children unmount
      // cleanly on "Try Again" and re-initialize their useState/useEffect
      // hooks from scratch.
      return (
        <React.Fragment key={this.state.retryKey}>
          {this.props.children}
        </React.Fragment>
      );
    }

    const env = this.state.envelope || {};
    return (
      <div
        data-testid="root-error-boundary"
        style={{
          minHeight: "100vh",
          backgroundColor: "#0f172a",
          color: "#e2e8f0",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
          fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
        }}
      >
        <div style={{ maxWidth: 480, width: "100%", textAlign: "center" }}>
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              backgroundColor: "rgba(239, 68, 68, 0.15)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 16px",
              fontSize: 28,
            }}
            aria-hidden="true"
          >
            ⚠
          </div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: "#f8fafc", margin: "0 0 8px" }}>
            Something went wrong
          </h1>
          <p style={{ color: "#94a3b8", fontSize: 14, lineHeight: 1.5, margin: "0 0 24px" }}>
            The app hit a snag rendering this screen. Your account and data are safe — this is just a UI hiccup. Try one of the recovery options below.
          </p>

          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
            <button
              type="button"
              onClick={this.handleTryAgain}
              data-testid="root-error-try-again"
              style={{
                width: "100%",
                padding: "12px 16px",
                borderRadius: 12,
                border: "none",
                background: "linear-gradient(to right, #06b6d4, #2563eb)",
                color: "white",
                fontWeight: 600,
                fontSize: 14,
                cursor: "pointer",
              }}
            >
              Try Again
            </button>
            <button
              type="button"
              onClick={this.handleHardReload}
              data-testid="root-error-hard-reload"
              style={{
                width: "100%",
                padding: "12px 16px",
                borderRadius: 12,
                border: "1px solid rgba(255,255,255,0.1)",
                background: "transparent",
                color: "#cbd5e1",
                fontWeight: 500,
                fontSize: 14,
                cursor: "pointer",
              }}
            >
              Reload App
            </button>
          </div>

          <div
            style={{
              borderRadius: 12,
              border: "1px solid rgba(239, 68, 68, 0.2)",
              background: "rgba(239, 68, 68, 0.05)",
              textAlign: "left",
            }}
            data-testid="root-error-details"
          >
            <button
              type="button"
              onClick={() => this.setState((s) => ({ showDetails: !s.showDetails }))}
              data-testid="root-error-details-toggle"
              style={{
                width: "100%",
                padding: "10px 12px",
                background: "transparent",
                border: "none",
                color: "#fda4a4",
                fontSize: 12,
                fontWeight: 500,
                cursor: "pointer",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span>{this.state.showDetails ? "Hide technical details" : "Show technical details"}</span>
              <span style={{ color: "rgba(252, 165, 165, 0.6)" }}>
                {this.state.showDetails ? "▾" : "▸"}
              </span>
            </button>
            {this.state.showDetails && (
              <div
                style={{
                  padding: "0 12px 12px",
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                  fontSize: 11,
                  color: "rgba(254, 226, 226, 0.9)",
                  wordBreak: "break-all",
                  lineHeight: 1.6,
                }}
                data-testid="root-error-details-content"
              >
                <div>name: <span style={{ color: "#fef2f2" }}>{env.name || "Error"}</span></div>
                <div>message: <span style={{ color: "#fef2f2" }}>{env.message || "(no message)"}</span></div>
                <div>pathname: <span style={{ color: "#fef2f2" }}>{env.pathname || "/"}</span></div>
                <div>backend: <span style={{ color: "#fef2f2" }}>{env.backendHost || "—"}</span></div>
                <div>ts: <span style={{ color: "#fef2f2" }}>{env.ts || "—"}</span></div>
                {env.componentStack && (
                  <div style={{ marginTop: 6 }}>
                    <div style={{ marginBottom: 2 }}>stack:</div>
                    <pre
                      style={{
                        whiteSpace: "pre-wrap",
                        margin: 0,
                        fontSize: 10,
                        color: "#fecaca",
                      }}
                    >
                      {env.componentStack}
                    </pre>
                  </div>
                )}
                <button
                  type="button"
                  onClick={this.handleCopy}
                  data-testid="root-error-details-copy"
                  aria-live="polite"
                  style={{
                    marginTop: 8,
                    padding: "4px 10px",
                    borderRadius: 6,
                    border: "1px solid rgba(248, 113, 113, 0.3)",
                    background: "rgba(239, 68, 68, 0.1)",
                    color: "#fef2f2",
                    fontSize: 11,
                    fontFamily: "system-ui, sans-serif",
                    fontWeight: 500,
                    cursor: "pointer",
                  }}
                >
                  {this.state.copyStatus === "copied"
                    ? "Copied!"
                    : this.state.copyStatus === "failed"
                      ? "Copy failed"
                      : "Copy details"}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
}

export default RootErrorBoundary;
